import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { DnsState, updateDnsEvent } from '@nx-frontend/shared/store/dns';
import { $menuState, setMenuWorkspaceIdEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { $isCustomizerMode } from '@nx-frontend/shared/store/portal-app';
import { MenuType, PortalThemeConfigPartial } from '@nx-frontend/shared/types';
import { MenuState } from '@nx-frontend/shared/types';

export interface PortalState {
  id: string;
  name: string;
  logo: string | null;
  domain: string;
  theme: PortalThemeConfigPartial;
  isPortalAccessDenied: boolean;
  isCustomizerAccessDenied: boolean;
}

export const initialPortalState: PortalState = {
  id: '',
  name: '',
  logo: null,
  domain: '',
  theme: {},
  /**
   * Whether current user has no access to the workspace portal
   */
  isPortalAccessDenied: false,
  isCustomizerAccessDenied: false,
};

interface PortalSidebarExpandItemState {
  expanded: boolean;
}

interface PortalsSidebarExpandState {
  [portalId: string]: {
    [itemId: string]: PortalSidebarExpandItemState;
  };
}

interface PortalForWebnotesDTO {
  menu: Record<MenuType, MenuState>;
  settings: PortalState;
  dns: DnsState;
}

const localStoragePortalExpandedItemsKey = 'portal-expanded-items';

/**
 * fetches portal menus and portal settings to display them in the webnotes sidebar
 */
export const fetchPortalForWebnotesFx = createEffect(async (workspaceId: string) => {
  const res = await axiosClient.get<PortalForWebnotesDTO>(`/v2/api/workspaces/${workspaceId}/portal`);
  return res.data;
});

export const fetchPortalExpandedItemsInSidebarFx = createEffect(() => {
  const raw = localStorage.getItem(localStoragePortalExpandedItemsKey);
  let parsed = {};
  try {
    if (raw) {
      parsed = JSON.parse(raw);
    }
  } catch (err) {
    console.error('Fail to parse portals items expanded state', err);
  }
  return Promise.resolve(parsed ? parsed : {});
});

const persistPortalExpandedInSidebarFx = createEffect((state: PortalsSidebarExpandState) => {
  return localStorage.setItem(localStoragePortalExpandedItemsKey, JSON.stringify(state));
});

const setPortalSettingsIsLoadingEvent = createEvent<boolean>();
const portalSettingsResetEvent = createEvent<boolean>();
export const $portalSettingsLoading = createStore(false).on(setPortalSettingsIsLoadingEvent, (state, value) => {
  return value;
});

export const $portalSettings = createStore<PortalState>(initialPortalState);
export const $portalsExpandedInSidebar = createStore<PortalsSidebarExpandState>({});

export const setPortalExpandedItemInSidebarEvent = createEvent<{
  portalId: string;
  itemId: string;
  expanded: boolean;
}>();

$portalsExpandedInSidebar.on(setPortalExpandedItemInSidebarEvent, (state, payload) => {
  const { portalId, itemId, expanded } = payload;
  const portalState = state[portalId] || {};
  const newState = {
    ...state,
    [portalId]: {
      ...portalState,
      [itemId]: {
        expanded,
      },
    },
  };
  return newState;
});

$portalsExpandedInSidebar.on(fetchPortalExpandedItemsInSidebarFx.doneData, (state, data) => {
  return data;
});

// Because fetchPortalForWebnotesFx returns portal settings as well we can subscribe to it and update portal settings
$portalSettings
  .on(fetchPortalForWebnotesFx.doneData, (state, { settings }) => {
    return Object.assign({}, settings);
  })
  .on(portalSettingsResetEvent, (state) => {
    return { ...initialPortalState, isCustomizerAccessDenied: state.isCustomizerAccessDenied };
  });

$menuState.on(fetchPortalForWebnotesFx.doneData, (state, { menu }) => {
  return {
    ...state,
    ...menu,
  };
});

// persists portal expanded items in sidebar when setPortalExpandedItemInSidebarEvent event occurs
sample({
  source: $portalsExpandedInSidebar,
  clock: setPortalExpandedItemInSidebarEvent,
  target: persistPortalExpandedInSidebarFx,
});

// when user switched to another workspace, we need to reset portal state and refetch portal details
sample({
  source: $isCustomizerMode,
  clock: setMenuWorkspaceIdEvent,
  filter: (isCustomizer) => !isCustomizer,
  fn: (_, eventPayload) => eventPayload,
  target: [fetchPortalForWebnotesFx, portalSettingsResetEvent],
});

sample({
  source: $isCustomizerMode,
  clock: setMenuWorkspaceIdEvent,
  filter: (isCustomizer) => !isCustomizer,
  fn: () => true,
  target: setPortalSettingsIsLoadingEvent,
});

// event emit when poral settings fetched
export const onPortalFetchedEvent = createEvent<PortalForWebnotesDTO>();
sample({
  clock: fetchPortalForWebnotesFx.doneData,
  target: onPortalFetchedEvent,
});

sample({
  clock: fetchPortalForWebnotesFx.doneData,
  fn: (data) => data.dns,
  target: updateDnsEvent,
});

sample({
  clock: [fetchPortalForWebnotesFx.doneData, fetchPortalForWebnotesFx.failData],
  fn: () => false,
  target: setPortalSettingsIsLoadingEvent,
});

export const setPortalExpandedItemsInListEvent = createEvent<{ portalId: string; itemIds: string[] }>();

$portalsExpandedInSidebar.on(setPortalExpandedItemsInListEvent, (state, payload) => {
  const { portalId, itemIds } = payload;
  const portalState = state[portalId] || {};

  itemIds.forEach((itemId) => {
    portalState[itemId] = {
      expanded: true,
    };
  });

  const newState = {
    ...state,
    [portalId]: portalState,
  };

  return newState;
});

sample({
  source: $portalsExpandedInSidebar,
  clock: setPortalExpandedItemsInListEvent,
  target: persistPortalExpandedInSidebarFx,
});
